import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import { getAudit, listAudits, unwrapGet } from "../../../helpers/auditHub_helper";
import { AUDIT_HUB_USE_MOCK, AUDIT_STATUSES } from "./constants";
import {
  fetchAudits,
  requestAuditReport,
  resetAuditReport,
} from "../../../store/auditHub/auditHubSlice";
import {
  buildComplianceControlsPath,
  frameworkKey,
  isApproachingCompletion,
  isStaleAudit,
} from "./utils";
import {
  AH_MUTED,
  AH_TEXT,
  AuditHubThemeProvider,
  accentOutlinedButtonSx,
  auditHubPageSx,
  primaryButtonSx,
  searchFieldSx,
  tableShellSx,
  toolbarRowSx,
} from "./theme";
import SummaryCards from "./components/SummaryCards";
import FilterMenu from "./components/FilterMenu";
import {
  ActiveAuditsTable,
  CompletedAuditsTable,
  HubEmptyState,
} from "./components/AuditsTable";
import AuditComplianceDialog from "./components/AuditComplianceDialog";
import EditAuditDialog from "./components/EditAuditDialog";
import DeleteAuditDialog from "./components/DeleteAuditDialog";
import CompleteAuditDialog from "./components/CompleteAuditDialog";
import CancelAuditDialog from "./components/CancelAuditDialog";
import LaunchAuditDialog from "./components/LaunchAuditDialog";
import ActivityTrailDialog from "./components/ActivityTrailDialog";
import FrameworkCatalogDialog from "./components/FrameworkCatalogDialog";

const auditCodeNumber = (audit) => {
  const match = /(\d+)$/.exec(audit?.auditCode || "");
  return match ? Number(match[1]) : -1;
};

const byNewestAuditCode = (a, b) =>
  auditCodeNumber(b) - auditCodeNumber(a) || String(b?.auditCode || "").localeCompare(String(a?.auditCode || ""));

const matchesQuery = (audit, query) => {
  if (!query) return true;
  const hay = [audit.auditCode, audit.auditId, audit.name, audit.ownerEmail, audit.status]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(query);
};

const AuditHub = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const list = useSelector((s) => s.auditHub?.list) || { items: [], status: "idle", error: null };
  const report = useSelector((s) => s.auditHub?.report) || {};
  const userRoles = useSelector((s) => s.profile?.userRoles);
  const isAdmin = Array.isArray(userRoles) && userRoles.includes("Administrator");
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [complianceTarget, setComplianceTarget] = useState({
    open: false,
    status: "idle",
    audit: null,
    error: null,
  });
  const [editAuditId, setEditAuditId] = useState(null);
  const [deleteAudit, setDeleteAudit] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [launchTarget, setLaunchTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState({ open: false, audit: null });
  const [activePage, setActivePage] = useState({ page: 0, size: 10 });
  const [activeList, setActiveList] = useState({ items: [], status: "idle", error: null });
  const [activeReload, setActiveReload] = useState(0);
  const [completedPage, setCompletedPage] = useState({ page: 0, size: 10 });
  const [completedList, setCompletedList] = useState({ items: [], status: "idle", error: null });
  const openRequest = useRef(0);

  useEffect(() => {
    document.title = "Assignment Hub";
  }, []);

  useEffect(() => {
    dispatch(fetchAudits({ page: 1, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    setActiveList((prev) => ({ ...prev, status: "loading", error: null }));
    // The status query takes a single value, so draft and active are fetched separately.
    Promise.all(
      [AUDIT_STATUSES.DRAFT, AUDIT_STATUSES.ACTIVE].map((status) =>
        listAudits({ status, page: 1, size: 100 }).then(unwrapGet)
      )
    )
      .then((bodies) => {
        if (cancelled) return;
        const items = bodies
          .flatMap((body) => (Array.isArray(body?.items) ? body.items : []))
          .sort(byNewestAuditCode);
        setActiveList({ items, status: "succeeded", error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setActiveList({
          items: [],
          status: "failed",
          error: error?.message || "Unable to load in-progress assignments.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [activeReload]);

  useEffect(() => {
    let cancelled = false;
    setCompletedList((prev) => ({ ...prev, status: "loading", error: null }));
    // The status query takes a single value, so completed and cancelled (archived) are fetched separately.
    Promise.all(
      [AUDIT_STATUSES.COMPLETED, AUDIT_STATUSES.CANCELLED].map((status) =>
        listAudits({ status, page: 1, size: 100 }).then(unwrapGet)
      )
    )
      .then((bodies) => {
        if (cancelled) return;
        const items = bodies.flatMap((body) => (Array.isArray(body?.items) ? body.items : []));
        setCompletedList({ items, status: "succeeded", error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setCompletedList({
          items: [],
          status: "failed",
          error: error?.message || "Unable to load completed assignments.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [activeReload]);

  const refreshAudits = () => {
    dispatch(fetchAudits({ page: 1, size: 100 }));
    setActiveReload((n) => n + 1);
  };

  useEffect(() => {
    const state = history.location.state;
    const failures = state?.scanDispatchFailures;
    if (Array.isArray(failures) && failures.length) {
      setSnackbar({
        open: true,
        message: `Assignment created. Scan dispatch: ${failures.join(" ")}`,
        severity: "warning",
      });
    } else if (state?.draftSaved) {
      setSnackbar({
        open: true,
        message:
          typeof state.draftSaved === "string" ? `Draft ${state.draftSaved} saved.` : "Draft saved.",
        severity: "success",
      });
    } else {
      return;
    }
    history.replace({ pathname: history.location.pathname, state: undefined });
  }, [history]);

  useEffect(() => {
    if (report.status === "succeeded" && report.downloadUrl) {
      window.open(report.downloadUrl, "_blank", "noopener,noreferrer");
      setSnackbar({ open: true, message: "Report is ready.", severity: "success" });
      dispatch(resetAuditReport());
    } else if (report.status === "failed" && report.error) {
      setSnackbar({ open: true, message: report.error, severity: "error" });
      dispatch(resetAuditReport());
    }
  }, [report.status, report.downloadUrl, report.error, dispatch]);

  const audits = useMemo(() => list.items || [], [list.items]);

  const activeRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return activeList.items.filter((audit) => {
      if (!matchesQuery(audit, q)) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(audit.status)) return false;
      return true;
    });
  }, [activeList.items, query, selectedStatuses]);
  const activeLastPage = Math.max(0, Math.ceil(activeRows.length / activePage.size) - 1);
  const activePageIndex = Math.min(activePage.page, activeLastPage);
  const activePageRows = activeRows.slice(
    activePageIndex * activePage.size,
    (activePageIndex + 1) * activePage.size
  );
  const completedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return completedList.items.filter((audit) => {
      if (!matchesQuery(audit, q)) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(audit.status)) return false;
      return true;
    });
  }, [completedList.items, query, selectedStatuses]);
  const completedLastPage = Math.max(0, Math.ceil(completedRows.length / completedPage.size) - 1);
  const completedPageIndex = Math.min(completedPage.page, completedLastPage);
  const completedPageRows = completedRows.slice(
    completedPageIndex * completedPage.size,
    (completedPageIndex + 1) * completedPage.size
  );

  const summarySource = useMemo(() => {
    const q = query.trim().toLowerCase();
    return audits.filter((audit) => matchesQuery(audit, q));
  }, [audits, query]);

  const counts = useMemo(() => {
    const active = summarySource.filter((a) => a.status === AUDIT_STATUSES.ACTIVE);
    return {
      inProgress: active.length,
      approaching: active.filter(isApproachingCompletion).length,
      stale: active.filter(isStaleAudit).length,
    };
  }, [summarySource]);

  const goCreate = () => history.push("/audit-hub/create");
  const openActivity = (audit) => setActivityTarget({ open: true, audit });

  const handleDownload = (audit) => {
    dispatch(requestAuditReport({ auditId: audit.auditId, format: "docx" }));
    setSnackbar({ open: true, message: `Generating report for ${audit.auditCode}…`, severity: "info" });
  };
  const goToFramework = (auditId, account, scope) => {
    const frameworkName = scope?.framework?.name;
    const runId = scope?.latestRunId;
    const accountNumber = account?.accountNumber;
    if (!frameworkName || !runId || !accountNumber || !auditId) return false;
    localStorage.setItem("selectedAccountNumber", accountNumber);
    if (scope.latestRunEndedAt) {
      localStorage.setItem("selectedScanDate", scope.latestRunEndedAt);
    }
    history.push(
      buildComplianceControlsPath({
        frameworkName,
        runId,
        accountNumber,
        auditId,
      })
    );
    return true;
  };
  const handleOpenAudit = (audit) => {
    const auditId = audit?.auditId;
    if (!auditId) return;
    const requestId = openRequest.current + 1;
    openRequest.current = requestId;
    getAudit(auditId)
      .then((res) => {
        if (openRequest.current !== requestId) return;
        const detail = unwrapGet(res);
        const pairs = [];
        (detail?.accounts || []).forEach((account) => {
          (account.scopes || []).forEach((scope) => pairs.push({ account, scope }));
        });
        const openable = pairs.filter(
          ({ scope }) => scope?.framework?.name && scope?.latestRunId
        );
        // The compliance page switches accounts itself, so only a choice of framework needs asking.
        const frameworks = new Set(openable.map(({ scope }) => frameworkKey(scope.framework)));
        if (frameworks.size === 1) {
          goToFramework(detail.auditId, openable[0].account, openable[0].scope);
          return;
        }
        if (!openable.length) {
          setSnackbar({
            open: true,
            message: "This assignment has no completed scan yet, so its framework view cannot be opened.",
            severity: "info",
          });
          return;
        }
        setComplianceTarget({ open: true, status: "ready", audit: detail, error: null });
      })
      .catch((error) => {
        if (openRequest.current !== requestId) return;
        setSnackbar({
          open: true,
          message: error?.message || "Unable to load this assignment.",
          severity: "error",
        });
      });
  };
  const openComplianceScope = (account, scope) => {
    if (!goToFramework(complianceTarget.audit?.auditId, account, scope)) return;
    setComplianceTarget((prev) => ({ ...prev, open: false }));
  };

  const loading = list.status === "loading" || list.status === "idle";
  const hasAny = audits.length > 0;
  const truncated = Number(list.total) > audits.length;

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={tableShellSx}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                }}
              >
                Assignment Hub
              </Typography>
              <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                Create and track manual-control assignments.
                {AUDIT_HUB_USE_MOCK
                  ? " Using the local OpenAPI mock until the service is deployed."
                  : ""}
              </Typography>
            </Box>

            <Box sx={tableShellSx}>
              <Box sx={{ ...toolbarRowSx, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search assignments..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...searchFieldSx,
                    flex: "245 1 0%",
                    minWidth: 160,
                    maxWidth: "100%",
                  }}
                />
                <Box sx={{ flex: "276 1 0%", minWidth: 0 }} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    gap: 1.25,
                    flexShrink: 0,
                  }}
                >
                  <FilterMenu
                    anchorEl={filterAnchor}
                    onOpen={(e) => setFilterAnchor(e.currentTarget)}
                    onClose={() => setFilterAnchor(null)}
                    selectedStatuses={selectedStatuses}
                    onToggleStatus={(status) => {
                      setSelectedStatuses((prev) =>
                        prev.includes(status) ? prev.filter((x) => x !== status) : [...prev, status]
                      );
                    }}
                    onClear={() => setSelectedStatuses([])}
                  />
                  {isAdmin ? (
                    <Button
                      variant="outlined"
                      startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 18 }} />}
                      onClick={() => setCatalogOpen(true)}
                      sx={{ ...accentOutlinedButtonSx, flexShrink: 0, whiteSpace: "nowrap" }}
                    >
                      Framework Catalog
                    </Button>
                  ) : null}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={goCreate}
                    sx={{ ...primaryButtonSx, flexShrink: 0 }}
                  >
                    Create New Assignment
                  </Button>
                </Box>
              </Box>

              {list.error ? (
                <Alert severity="error" sx={{ borderRadius: "10px" }}>
                  {list.error}
                </Alert>
              ) : loading && !hasAny ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !hasAny ? (
                <HubEmptyState />
              ) : (
                <>
                  {truncated ? (
                    <Alert severity="info" sx={{ borderRadius: "10px", mb: 2 }}>
                      Showing {audits.length} of {list.total} assignments.
                    </Alert>
                  ) : null}
                  <SummaryCards counts={counts} />
                  <ActiveAuditsTable
                    rows={activePageRows}
                    total={activeRows.length}
                    page={activePageIndex}
                    rowsPerPage={activePage.size}
                    loading={activeList.status === "loading" || activeList.status === "idle"}
                    error={activeList.error}
                    onPageChange={(page) => setActivePage((prev) => ({ ...prev, page }))}
                    onRowsPerPageChange={(size) => setActivePage({ page: 0, size })}
                    onOpenAudit={handleOpenAudit}
                    onEdit={(audit) => setEditAuditId(audit.auditId)}
                    onComplete={setCompleteTarget}
                    onCancel={setCancelTarget}
                    onDelete={setDeleteAudit}
                    onActivity={openActivity}
                    onLaunch={setLaunchTarget}
                  />
                  <CompletedAuditsTable
                    rows={completedPageRows}
                    total={completedRows.length}
                    page={completedPageIndex}
                    rowsPerPage={completedPage.size}
                    loading={completedList.status === "loading" || completedList.status === "idle"}
                    error={completedList.error}
                    onPageChange={(page) => setCompletedPage((prev) => ({ ...prev, page }))}
                    onRowsPerPageChange={(size) => setCompletedPage({ page: 0, size })}
                    onDownload={handleDownload}
                    onActivity={openActivity}
                  />
                </>
              )}
            </Box>
          </Box>
        </Container>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <DeleteAuditDialog
        open={Boolean(deleteAudit)}
        audit={deleteAudit}
        onClose={() => setDeleteAudit(null)}
        onDeleted={() => {
          setDeleteAudit(null);
          refreshAudits();
        }}
      />
      <CompleteAuditDialog
        open={Boolean(completeTarget)}
        audit={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onCompleted={(audit) => {
          setCompleteTarget(null);
          refreshAudits();
          setSnackbar({ open: true, message: `${audit.auditCode || "Assignment"} completed.`, severity: "success" });
        }}
      />
      <CancelAuditDialog
        open={Boolean(cancelTarget)}
        audit={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={(audit) => {
          setCancelTarget(null);
          refreshAudits();
          setSnackbar({ open: true, message: `${audit.auditCode || "Assignment"} cancelled.`, severity: "success" });
        }}
      />
      <LaunchAuditDialog
        open={Boolean(launchTarget)}
        audit={launchTarget}
        onClose={() => setLaunchTarget(null)}
        onLaunched={(audit, failures) => {
          setLaunchTarget(null);
          refreshAudits();
          const code = audit.auditCode || "Assignment";
          setSnackbar(
            failures.length
              ? { open: true, message: `${code} launched. Scan dispatch: ${failures.join(" ")}`, severity: "warning" }
              : { open: true, message: `${code} launched.`, severity: "success" }
          );
        }}
      />
      <EditAuditDialog
        open={Boolean(editAuditId)}
        auditId={editAuditId}
        onClose={() => setEditAuditId(null)}
        onRefresh={refreshAudits}
        onSaved={() => {
          setEditAuditId(null);
          refreshAudits();
        }}
      />
      <AuditComplianceDialog
        open={complianceTarget.open}
        status={complianceTarget.status}
        audit={complianceTarget.audit}
        error={complianceTarget.error}
        onClose={() => setComplianceTarget((prev) => ({ ...prev, open: false }))}
        onOpenScope={openComplianceScope}
      />
      <ActivityTrailDialog
        open={activityTarget.open}
        audit={activityTarget.audit}
        onClose={() => setActivityTarget((prev) => ({ ...prev, open: false }))}
      />
      {isAdmin ? <FrameworkCatalogDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} /> : null}
    </AuditHubThemeProvider>
  );
};

export default AuditHub;
